import fetch from 'app/utils/requests/fetch';
import { type OneFiitTokenResponse, parseOneFiitTokenResponse } from 'app/services/one-fiit';
import { CODE_CHALLENGE_METHOD, generateCodeChallenge, generateCodeVerifier } from 'app/utils/pkce';
import urlQueryParams from 'app/utils/requests/query-params';

type CreateAuthArgs = {
  url: string;
  clientId: string;
  clientSecret: string;
  oneFiitAuthUrl?: string;
  oneFiitClientId?: string;
  oneFiitAuthCodeUrl?: string;
  authCallbackRedirectUrl?: string;
};

export type TokenPayload = {
  accessToken: string;
  accessTokenExpiresAt?: number | string;
  refreshToken: string;
  refreshTokenExpiresAt?: string;
};

type SigninCredentials = {
  username: string;
  password: string;
};

type GetAuthCodeResponse = {
  authorizationCode: string;
  // Only set by the one-fiit mint branch: the PKCE verifier the code was bound to, which the
  // exchange leg needs in place of the (no longer available) client secret.
  codeVerifier?: string;
};

type GetTokenWithAuthCodeResponse = {
  accessToken: string;
  redirectUri: string;
};

type ExchangeCodeForTokenArgs = {
  code: string;
  redirectUri: string;
  codeVerifier?: string;
};

type RefreshTokenArgs = { refreshToken: string };

const createAuthService = ({
  url,
  clientId,
  clientSecret,
  oneFiitAuthUrl,
  oneFiitClientId,
  oneFiitAuthCodeUrl,
  authCallbackRedirectUrl,
}: CreateAuthArgs) => {
  if (!url) {
    throw new Error('No url to auth service supplied');
  }

  if (!clientId) {
    throw new Error('No client id for auth service supplied');
  }

  if (!clientSecret) {
    throw new Error('No client secret for auth service supplied');
  }

  if (oneFiitAuthUrl && !oneFiitClientId) {
    throw new Error('No client id for the one-fiit auth service supplied');
  }

  // Minting at one-fiit also needs the exchange leg (a one-fiit code is only redeemable there) and
  // the redirect uri that gets bound into the code. Anything short of the full set stays dark, so a
  // half-applied env flip degrades to the legacy hyrox-api paths instead of breaking sign-in.
  const oneFiitAuthCode =
    oneFiitAuthCodeUrl && oneFiitAuthUrl && authCallbackRedirectUrl
      ? { mintUrl: oneFiitAuthCodeUrl, redirectUri: authCallbackRedirectUrl }
      : null;

  if (oneFiitAuthCodeUrl && !oneFiitAuthCode) {
    console.error(
      'REACT_APP_ONE_FIIT_AUTH_CODE_URL is set without the one-fiit auth url and auth callback redirect url it needs; the iframe auth code flow stays on hyrox-api',
    );
  }

  const signin = async ({ username, password }: SigninCredentials): Promise<TokenPayload> => {
    const params = urlQueryParams({
      client_id: clientId,
      grant_type: 'password',
      client_secret: clientSecret,
      username,
      password,
    });

    const { body } = await fetch(url, {
      body: params,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      method: 'POST',
    });

    return body as TokenPayload;
  };

  const refreshToken = async ({ refreshToken }: RefreshTokenArgs): Promise<TokenPayload> => {
    if (oneFiitAuthUrl) {
      const params = urlQueryParams({
        client_id: oneFiitClientId,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      });

      const { body } = await fetch(oneFiitAuthUrl, {
        body: params,
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
        },
        method: 'POST',
      });

      return parseOneFiitTokenResponse(body as OneFiitTokenResponse);
    }

    const params = urlQueryParams({
      client_id: clientId,
      grant_type: 'refresh_token',
      client_secret: clientSecret,
      refresh_token: refreshToken,
    });

    const { body } = await fetch(url, {
      body: params,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      method: 'POST',
    });

    return body as TokenPayload;
  };

  const getAuthCode = async (
    authCodeUrl: string,
    authorization: string,
    clientId: string,
  ): Promise<GetAuthCodeResponse> => {
    // The mint moves to one-fiit wholesale, so the hyrox endpoint the caller passes does not apply.
    if (oneFiitAuthCode) {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      const oneFiitParams = urlQueryParams({
        client_id: clientId,
        redirect_uri: oneFiitAuthCode.redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: CODE_CHALLENGE_METHOD,
        state: Math.random().toString(36).substring(2, 15),
      });

      const { body } = await fetch(oneFiitAuthCode.mintUrl, {
        body: oneFiitParams,
        headers: {
          Authorization: authorization,
          'content-type': 'application/x-www-form-urlencoded',
        },
        method: 'POST',
      });

      return { authorizationCode: (body as GetAuthCodeResponse).authorizationCode, codeVerifier };
    }

    const params = urlQueryParams({
      client_id: clientId,
      response_type: 'code',
      state: Math.random().toString(36).substring(2, 15),
      scope: 'read',
    });

    const url = `${authCodeUrl}?${params}`;

    const { body } = await fetch(url, {
      headers: {
        Authorization: authorization,
      },
      method: 'GET',
    });

    return body as GetAuthCodeResponse;
  };

  const getTokenWithAuthCode = async (
    authCodeUrl: string,
    authorization: string,
    clientId: string,
    app: string,
    partnerClientId: string,
  ): Promise<GetTokenWithAuthCodeResponse> => {
    const params = urlQueryParams({
      client_id: clientId,
      response_type: 'code',
      state: Math.random().toString(36).substring(2, 15),
      scope: 'read',
      app,
      partnerClientId,
    });

    const url = `${authCodeUrl}?${params}`;

    const { body } = await fetch(url, {
      headers: {
        Authorization: authorization,
      },
      method: 'GET',
    });

    return body as GetTokenWithAuthCodeResponse;
  };

  const exchangeCodeForToken = async ({
    code,
    redirectUri,
    codeVerifier,
  }: ExchangeCodeForTokenArgs): Promise<TokenPayload> => {
    // A verifier means the code was minted by one-fiit, which takes it in place of the client
    // secret hyrox-app no longer holds once hyrox-app-client becomes a public client.
    //
    // client_id must be the one the code was minted for (oauth2-server rejects a code presented by
    // any other client), which the mint endpoint whitelists as hyrox-app-client — so it is clientId
    // here, not oneFiitClientId. That binds the issued token to clientId, and refreshToken above
    // sends oneFiitClientId, which oauth2-server likewise rejects as "issued to another client".
    // REACT_APP_AUTH_CLIENT_ID and REACT_APP_CLIENT_ID_FOR_ONE_FIIT therefore have to hold the same
    // value once this flow goes live, or sessions from it die at the first refresh (JS-9139).
    if (oneFiitAuthUrl && codeVerifier) {
      const oneFiitParams = urlQueryParams({
        client_id: clientId,
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
      });

      const { body } = await fetch(oneFiitAuthUrl, {
        body: oneFiitParams,
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
        },
        method: 'POST',
      });

      return parseOneFiitTokenResponse(body as OneFiitTokenResponse);
    }

    const params = urlQueryParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      client_secret: clientSecret,
      code,
      redirect_uri: redirectUri,
      state: Math.random().toString(36).substring(2, 15),
    });

    const { body } = await fetch(url, {
      body: params,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      method: 'POST',
    });

    return body as TokenPayload;
  };

  return {
    signin,
    refreshToken,
    getAuthCode,
    getTokenWithAuthCode,
    exchangeCodeForToken,
  };
};

export type AuthService = ReturnType<typeof createAuthService>;

export default createAuthService;
